import React from "react";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import { Globe } from "lucide-react";
import Footer from "../components/Footer";
import { orgProfile, socialsData } from "../data/org";

function IconGithub({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}

function IconLinkedin({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconTwitter({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function IconInstagram({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

const iconMap: Record<string, React.FC<any>> = {
  github: IconGithub,
  linkedin: IconLinkedin,
  twitter: IconTwitter,
  instagram: IconInstagram,
  portfolio: Globe,
};

const socials = socialsData.map((s) => ({
  ...s,
  icon: iconMap[s.iconId],
}));

export default function About() {
  return (
    <>
      <SEO 
        title="About — Solarch"
        description="Meet the core team and maintainers behind Solarch."
        canonical="/about"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Solarch",
            "url": "https://solarch.in/about",
            "description": "Meet the core team and maintainers behind Solarch.",
            "mainEntity": {
              "@type": "Organization",
              "name": "Solarch",
              "url": "https://solarch.in",
              "logo": "https://solarch.in/solarch-logo.svg",
              "sameAs": [
                "https://github.com/xvertere-org/Solarch"
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Solarch Core Team",
            "itemListElement": [
              {
                "@type": "Person",
                "name": "Manas Mishra",
                "jobTitle": "Lead Maintainer",
                "url": "https://github.com/ManasMishraAKaThunder"
              },
              {
                "@type": "Person",
                "name": "Jay Suryawanshi",
                "jobTitle": "Project Initiator and Developer",
                "url": "https://github.com/Jay-Suryawansh7"
              },
              {
                "@type": "Person",
                "name": "Shreyash Dange",
                "jobTitle": "AI Developer",
                "url": "https://github.com/Yashuuuu02"
              },
              {
                "@type": "Person",
                "name": "Om Mishra",
                "jobTitle": "Backend Developer and Tester",
                "url": "https://github.com/ommishra2008a-tech"
              },
              {
                "@type": "Person",
                "name": "Sarthak Sadaphale",
                "jobTitle": "Brand Designer and Marketing"
              }
            ]
          }
        ]}
      />

      <div className="min-h-screen bg-theme-body pt-14 flex flex-col">
        {/* Hero / About Company */}
        <section className="relative overflow-hidden">
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <div className="flex flex-col items-center gap-10 text-center lg:flex-row lg:text-left">
              {/* Avatar */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="shrink-0"
              >
                <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full bg-white text-4xl font-bold text-white shadow-xl border-4 border-orange-500">
                  {orgProfile.avatarImageUrl ? (
                    <img src={orgProfile.avatarImageUrl} alt={orgProfile.name} className="h-full w-full object-cover scale-[1.20]" />
                  ) : (
                    orgProfile.avatarInitials
                  )}
                </div>
              </motion.div>

              {/* Text */}
              <div className="flex-1">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >

                  <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-theme sm:text-5xl">
                    {orgProfile.name}
                  </h1>
                  <p
                    className="mt-4 max-w-2xl text-theme-tertiary"
                    dangerouslySetInnerHTML={{ __html: orgProfile.bio }}
                  />
                </motion.div>

                {/* Socials */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start"
                >
                  {socials.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 rounded-lg border border-theme bg-theme-muted px-4 py-2 text-sm text-theme-secondary transition-all hover:border-theme-hover hover:bg-theme-surface hover:text-theme"
                    >
                      <s.icon className="h-4 w-4" />
                      {s.name}
                    </a>
                  ))}
                </motion.div>


              </div>
            </div>
          </div>
        </section>

        {/* Maintainers Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 flex-1">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <h2 className="font-heading text-2xl font-bold text-theme">
                Core Team
              </h2>
              <p className="mt-2 text-theme-tertiary text-lg">
                The people behind the code.
              </p>
            </motion.div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  name: "Manas Mishra",
                  role: "Lead Maintainer",
                  timeline: "June 2026 – Present",
                  description: "Actively maintaining and developing the Solarch codebase, contributing to its architecture, stability, and ongoing feature development.",
                  image: "/manas.jpeg",
                  github: "https://github.com/ManasMishraAKaThunder",
                  linkedin: "https://www.linkedin.com/in/manas-mishra-aa29a2381?utm_source=share_via&utm_content=profile&utm_medium=member_android"
                },
                {
                  name: "Jay Suryawanshi",
                  role: "Project Initiator and Developer",
                  timeline: "",
                  description: "Leading the project vision and driving development to build innovative solutions.",
                  image: "/jay.jpeg",
                  github: "https://github.com/Jay-Suryawansh7",
                  linkedin: "https://www.linkedin.com/in/jay-suryawanshi-200716jay?utm_source=share_via&utm_content=profile&utm_medium=member_android"
                },
                {
                  name: "Shreyash Dange",
                  role: "AI Developer",
                  timeline: "",
                  description: "Developing and integrating AI-driven solutions to enhance Solarch's capabilities.",
                  image: "/shreyash.jpeg",
                  github: "https://github.com/Yashuuuu02",
                  linkedin: "https://www.linkedin.com/in/shreyash-dange02/"
                },
                {
                  name: "Om Mishra",
                  role: "Backend Developer and Tester",
                  timeline: "",
                  description: "Ensuring robust, secure, and scalable server-side architecture along with comprehensive testing.",
                  image: "/om.jpeg",
                  github: "https://github.com/ommishra2008a-tech",
                  linkedin: "www.linkedin.com/in/om-mishra-188470399"
                },
                {
                  name: "Sarthak Sadaphale",
                  role: "Brand Designer and Marketing",
                  timeline: "",
                  description: "Driving brand identity and marketing strategies to effectively communicate the Solarch vision.",
                  image: "/sarthak.jpeg",
                  github: "",
                  linkedin: ""
                }
              ].map((member, i) => (
                <motion.div
                  key={i}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="rounded-[32px] border border-theme bg-theme-surface p-4 group hover:-translate-y-1 transition-all hover:border-theme-hover hover:bg-theme-muted"
                >
                  <div className="aspect-square rounded-2xl mb-5 overflow-hidden bg-theme-muted flex items-center justify-center text-5xl font-bold text-theme-tertiary">
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-[center_30%] group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      member.name.charAt(0)
                    )}
                  </div>
                  <div className="px-2">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-theme group-hover:text-primary transition-colors">{member.name}</h3>
                        <p className="text-theme-secondary text-sm mt-1">{member.role}</p>
                        {member.timeline && (
                          <p className="text-theme-muted text-xs mt-0.5 font-medium tracking-wide uppercase">{member.timeline}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {member.github && (
                          <a href={member.github} target="_blank" rel="noreferrer" className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-theme bg-theme-body text-theme-tertiary transition-all hover:border-primary/50 hover:bg-primary/10 hover:text-primary" aria-label={`${member.name} GitHub`}>
                            <IconGithub className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {member.linkedin && (
                          <a href={member.linkedin} target="_blank" rel="noreferrer" className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-theme bg-theme-body text-theme-tertiary transition-all hover:border-primary/50 hover:bg-primary/10 hover:text-primary" aria-label={`${member.name} LinkedIn`}>
                            <IconLinkedin className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                    {member.description && (
                      <>
                        <div className="border-t border-dashed border-theme mt-4 mb-5"></div>
                        <p className="text-theme-tertiary text-sm leading-relaxed">
                          {member.description}
                        </p>
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Footer />
        </div>
      </div>
    </>
  );
}
